// createSaxSegments.ts
import type { Point, PointArray } from '../types/contour-definition-array-type';

const EPS = 1e-9;

const mod = (x: number, n: number): number => ((x % n) + n) % n;

/**
 * Continuous position of a point on a closed polygon, expressed as
 * `edgeIndex + t` (t in [0,1)). Picks the edge with the smallest
 * perpendicular distance, so chord endpoints map back exactly onto the
 * edge they came from.
 */
function locateOnPolygon(p: Point, poly: PointArray): number {
  const n = poly.length;
  let bestPos = 0;
  let bestDist = Infinity;
  for (let e = 0; e < n; e++) {
    const a = poly[e];
    const b = poly[(e + 1) % n];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const len2 = abx * abx + aby * aby;
    let t = len2 > 0 ? ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const dx = p.x - (a.x + t * abx);
    const dy = p.y - (a.y + t * aby);
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      bestPos = e + t;
    }
  }
  return bestPos;
}

function pointAt(poly: PointArray, pos: number): Point {
  const n = poly.length;
  const e = mod(Math.floor(pos), n);
  const t = pos - Math.floor(pos);
  const a = poly[e];
  const b = poly[(e + 1) % n];
  return { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
}

/**
 * Vertices of `poly` from continuous position `a` to `b`, inclusive of the
 * exact interpolated endpoints, walking forward (increasing index) or
 * backward. Real polygon vertices strictly between a and b are kept verbatim.
 */
function arcPoints(
  poly: PointArray,
  a: number,
  b: number,
  forward: boolean,
): PointArray {
  const n = poly.length;
  const pts: PointArray = [pointAt(poly, a)];
  if (forward) {
    const dist = mod(b - a, n);
    for (let k = Math.floor(a) + 1; mod(k - a, n) < dist - EPS; k++) {
      pts.push(poly[mod(k, n)]);
    }
  }
  else {
    const dist = mod(a - b, n);
    for (let k = Math.ceil(a) - 1; mod(a - k, n) < dist - EPS; k--) {
      pts.push(poly[mod(k, n)]);
    }
  }
  pts.push(pointAt(poly, b));
  return pts;
}

/**
 * Arc between two chords (positions[i] -> positions[j]) along `poly`,
 * choosing the direction that contains none of the other chord positions.
 * Winding-agnostic and order-agnostic.
 */
function arcBetweenChords(
  poly: PointArray,
  positions: number[],
  i: number,
  j: number,
): PointArray {
  const n = poly.length;
  const a = positions[i];
  const b = positions[j];
  const forwardDist = mod(b - a, n);
  const forwardClear = positions.every((p, k) => {
    if (k === i || k === j) return true;
    const rel = mod(p - a, n);
    return rel <= EPS || rel >= forwardDist - EPS;
  });
  return arcPoints(poly, a, b, forwardClear);
}

/** Interior points of a (dense) chord, excluding its endpoints. */
function chordInterior(chord: PointArray): PointArray {
  return chord.length > 2 ? chord.slice(1, -1) : [];
}

/**
 * Build SAX segment polygons from chord lines and the endo/epi rings.
 *
 * Chords may be dense polylines: only their endpoints (`c[0]` = endo,
 * `c[c.length - 1]` = epi) are used to locate the arc ranges, and their
 * interior points are inserted into the chord seams so the chord edges
 * keep their full resolution.
 *
 * @param chordLines ordered chords (endo -> epi), as produced by
 *                   createSaxChordLines (possibly densified)
 * @param endo       closed endocardial polygon (raw coordinates)
 * @param epi        closed epicardial polygon (raw coordinates)
 * @returns one closed polygon per segment, between consecutive chords,
 *          using the exact endpoints and polygon/chord vertices
 */
export function createSaxSegments(
  chordLines: PointArray[],
  endo: PointArray,
  epi: PointArray,
): PointArray[] {
  const count = chordLines.length;
  if (count < 2 || endo.length < 3 || epi.length < 3) return [];

  const endoPos = chordLines.map(c => locateOnPolygon(c[0], endo));
  const epiPos = chordLines.map(c => locateOnPolygon(c[c.length - 1], epi));

  const segments: PointArray[] = [];
  for (let i = 0; i < count; i++) {
    const j = (i + 1) % count;

    const epiArc = arcBetweenChords(epi, epiPos, i, j); // epi_i .. epi_j
    const endoArc = arcBetweenChords(endo, endoPos, i, j); // endo_i .. endo_j

    const chordJ = chordLines[j]; // endo_j .. epi_j (dense)
    const chordI = chordLines[i]; // endo_i .. epi_i (dense)

    // loop: epi_i..epi_j ->(chord j: epi_j->endo_j)-> endo_j..endo_i
    //       ->(chord i: endo_i->epi_i)-> back to epi_i
    segments.push([
      ...epiArc, // epi_i .. epi_j
      ...[...chordJ].reverse().slice(1, -1), // epi_j -> endo_j interior pts
      ...endoArc.reverse(), // endo_j .. endo_i
      ...chordInterior(chordI), // endo_i -> epi_i interior pts (closing seam)
    ]);
  }

  return segments;
}
